import { cn, Input } from "@devhop/ui";
import { useEffect, useState } from "react";
import { usePatchProjectTaskProgressMutation } from "../api/queries";

type Props = {
  taskId: string;
  progressPercent: number | null;
  className?: string;
};

function clampPercent(n: number): number {
  return Math.min(100, Math.max(0, Math.round(n)));
}

export function ProjectAssignedTaskProgressField({
  taskId,
  progressPercent,
  className,
}: Props) {
  const mutation = usePatchProjectTaskProgressMutation();
  const busy =
    mutation.isPending &&
    mutation.variables != null &&
    mutation.variables.id === taskId;

  const [draft, setDraft] = useState(() =>
    progressPercent == null ? "" : String(progressPercent),
  );

  useEffect(() => {
    setDraft(progressPercent == null ? "" : String(progressPercent));
  }, [progressPercent]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed === "") {
      if (progressPercent == null) return;
      mutation.mutate({ id: taskId, progressPercent: null });
      return;
    }
    const n = Number.parseInt(trimmed, 10);
    if (Number.isNaN(n)) {
      setDraft(progressPercent == null ? "" : String(progressPercent));
      return;
    }
    const next = clampPercent(n);
    setDraft(String(next));
    const prev = progressPercent == null ? null : clampPercent(progressPercent);
    if (next === prev) return;
    mutation.mutate({ id: taskId, progressPercent: next });
  };

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 text-muted-foreground text-xs",
        className,
      )}
    >
      <span className="whitespace-nowrap">ຄວາມຄືບໜ້າ</span>
      <Input
        type="number"
        min={0}
        max={100}
        step={1}
        disabled={busy}
        className="h-8 w-20 px-2 text-right tabular-nums"
        aria-label="ເປີເຊັນຕ໌ຄວາມຄືບໜ້າ"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.currentTarget.blur();
          }
        }}
      />
      <span className="text-muted-foreground">%</span>
    </div>
  );
}
